// src/pages/Servers.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ServerModal from '../components/ServerModal';
import Card from '../components/Card';
import PageHeader from '../components/PageHeader';
import PrimaryButton from '../components/PrimaryButton';
import SecondaryButton from '../components/SecondaryButton';
import SearchInput from '../components/SearchInput';
import SortSelect from '../components/SortSelect';
import StatusBadge from '../components/StatusBadge';
import StatusSegmented from '../components/StatusSegmented';
import EmptyState from '../components/EmptyState';
import ErrorAlert from '../components/ErrorAlert';
import { SkeletonTableRows, SkeletonListToolbar, SkeletonListCard } from '../components/Skeleton';
import LoadingAnnouncer from '../components/LoadingAnnouncer';
import DeleteModal from '../components/DeleteModal';
import { HeaderCell, BodyCell } from '../components/TableCell';
import {
  IconPlus, IconRefresh, IconGrip, IconArrowUp, IconArrowDown,
  IconEdit, IconTrash, IconServer,
} from '../icons';
import { formatDate } from '../utils/format';

const COL = {
  handle: 52,
  name: 220,
  status: 124,
  url: 260,
  ip: 140,
  checked: 190,
  unhealthy: 190,
  actions: 96,
};

const SORT_OPTIONS = [
  { value: 'status', label: 'Status first' },
  { value: 'manual', label: 'Manual order' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'name_desc', label: 'Name Z–A' },
  { value: 'checked_desc', label: 'Last checked' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'unhealthy', label: 'Unhealthy' },
  { value: 'unknown', label: 'Unknown' },
];

const STATUS_PRIORITY = { unhealthy: 0, unknown: 1, healthy: 2 };

const ROW = 'flex items-stretch h-14 border-b border-line/60 last:border-b-0 hover:bg-surface transition-colors';

// Те же колонки, что у SortableServerRow: ширины из COL, плашки — по типичной
// длине содержимого (бейдж статуса — пилюля, даты — почти во всю ячейку).
const SKELETON_COLUMNS = [
  { width: COL.handle, first: true, fill: 30, height: 16 },
  { width: COL.name, grow: true, align: 'left', fill: 55, height: 14 },
  { width: COL.status, fill: 65, height: 24, radius: 'full' },
  { width: COL.url, grow: true, align: 'left', fill: 70 },
  { width: COL.ip, fill: 70 },
  { width: COL.checked, fill: 75 },
  { width: COL.unhealthy, fill: 75 },
  { width: COL.actions, sticky: 'right', fill: 60, height: 16 },
];

const SortableServerRow = ({
  server, index, totalCount, dragEnabled,
  onNavigate, onEdit, onDelete, onMoveUp, onMoveDown,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: server._id, disabled: !dragEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${ROW} cursor-pointer`}
      onClick={() => onNavigate(`/servers/${server._id}`)}
    >
      <BodyCell width={COL.handle} first>
        {dragEnabled ? (
          <div className="flex flex-col items-center">
            <div
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              className="text-ink-muted hover:text-ink cursor-grab active:cursor-grabbing"
              aria-label="Drag to reorder"
            >
              <IconGrip size={16} />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onMoveUp(index); }}
              disabled={index === 0}
              aria-label="Move up"
              className="w-7 h-7 inline-flex items-center justify-center rounded text-ink-muted hover:text-ink disabled:text-ink-disabled disabled:cursor-not-allowed"
            >
              <IconArrowUp size={12} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onMoveDown(index); }}
              disabled={index === totalCount - 1}
              aria-label="Move down"
              className="w-7 h-7 inline-flex items-center justify-center rounded text-ink-muted hover:text-ink disabled:text-ink-disabled disabled:cursor-not-allowed"
            >
              <IconArrowDown size={12} />
            </button>
          </div>
        )}
      </BodyCell>

      <BodyCell width={COL.name} grow align="left">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-ink truncate">{server.name}</p>
          {server.description && (
            <p className="text-[11px] text-ink-muted truncate">{server.description}</p>
          )}
        </div>
      </BodyCell>

      <BodyCell width={COL.status}>
        <StatusBadge status={server.lastCheckStatus} />
      </BodyCell>

      <BodyCell width={COL.url} grow align="left">
        <a
          href={server.healthRoute}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-brand-500 hover:underline truncate"
          title={server.healthRoute}
        >
          {server.healthRoute}
        </a>
      </BodyCell>

      <BodyCell width={COL.ip}>
        <span className="font-mono">
          {server.ipAddress || <span className="text-ink-muted">—</span>}
        </span>
      </BodyCell>

      <BodyCell width={COL.checked}>
        <span className="text-ink-muted whitespace-nowrap">
          {formatDate(server.lastCheckedAt)}
        </span>
      </BodyCell>

      <BodyCell width={COL.unhealthy}>
        <span className="text-ink-muted whitespace-nowrap">
          {formatDate(server.lastUnhealthyCheckAt)}
        </span>
      </BodyCell>

      <BodyCell width={COL.actions} sticky="right">
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(server); }}
            aria-label="Edit server"
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-brand-500 hover:bg-surface transition-colors"
          >
            <IconEdit size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(server); }}
            aria-label="Delete server"
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-danger hover:bg-surface transition-colors"
          >
            <IconTrash size={16} />
          </button>
        </div>
      </BodyCell>
    </div>
  );
};

const Servers = () => {
  const navigate = useNavigate();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('status');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/servers');
      setServers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching servers:', err);
      setError('Failed to load servers');
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => ({
    all: servers.length,
    healthy: servers.filter((s) => s.lastCheckStatus === 'healthy').length,
    unhealthy: servers.filter((s) => s.lastCheckStatus === 'unhealthy').length,
    unknown: servers.filter((s) => s.lastCheckStatus !== 'healthy' && s.lastCheckStatus !== 'unhealthy').length,
  }), [servers]);

  // Search / filter / sort are applied client-side — the list is small and the
  // API returns everything in one call.
  const visibleServers = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = servers.filter((s) => {
      if (statusFilter !== 'all') {
        const status = s.lastCheckStatus === 'healthy' || s.lastCheckStatus === 'unhealthy'
          ? s.lastCheckStatus
          : 'unknown';
        if (status !== statusFilter) return false;
      }
      if (!q) return true;
      return [s.name, s.description, s.healthRoute, s.ipAddress]
        .some((v) => v && v.toLowerCase().includes(q));
    });

    if (sort === 'status') {
      list = [...list].sort((a, b) =>
        (STATUS_PRIORITY[a.lastCheckStatus] ?? 1) - (STATUS_PRIORITY[b.lastCheckStatus] ?? 1));
    } else if (sort === 'name_asc') {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === 'name_desc') {
      list = [...list].sort((a, b) => b.name.localeCompare(a.name));
    } else if (sort === 'checked_desc') {
      list = [...list].sort((a, b) =>
        new Date(b.lastCheckedAt || 0) - new Date(a.lastCheckedAt || 0));
    }
    return list;
  }, [servers, search, sort, statusFilter]);

  // Reordering only makes sense while the list mirrors the stored order.
  const dragEnabled = sort === 'manual' && statusFilter === 'all' && !search.trim();

  const handleCreateServer = () => {
    setEditingServer(null);
    setIsModalOpen(true);
  };

  const handleEditServer = (server) => {
    setEditingServer(server);
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    const server = deleteTarget;
    setDeleteTarget(null);
    if (!server) return;
    try {
      await axios.delete(`/api/servers/${server._id}`);
      fetchServers();
    } catch (err) {
      console.error('Error deleting server:', err);
      setError('Failed to delete server');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingServer(null);
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    setEditingServer(null);
    fetchServers();
  };

  const handleCheckAll = async () => {
    try {
      setCheckingAll(true);
      await axios.post('/api/servers/check-all');
      // Wait a bit for checks to complete, then refresh
      setTimeout(() => {
        fetchServers();
        setCheckingAll(false);
      }, 2000);
    } catch (err) {
      console.error('Error checking all servers:', err);
      setError('Failed to check all servers');
      setCheckingAll(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const persistOrder = async (ordered) => {
    setServers(ordered);
    try {
      await axios.put('/api/servers/reorder', {
        servers: ordered.map((server, index) => ({ id: server._id, order: index })),
      });
    } catch (err) {
      console.error('Error updating server order:', err);
      fetchServers();
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = servers.findIndex((s) => s._id === active.id);
    const newIndex = servers.findIndex((s) => s._id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    persistOrder(arrayMove(servers, oldIndex, newIndex));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    persistOrder(arrayMove(servers, index, index - 1));
  };

  const handleMoveDown = (index) => {
    if (index === servers.length - 1) return;
    persistOrder(arrayMove(servers, index, index + 1));
  };

  const addServerButton = (
    <PrimaryButton onClick={handleCreateServer}>
      <IconPlus size={14} />
      Add server
    </PrimaryButton>
  );

  const actions = (
    <>
      <SecondaryButton onClick={handleCheckAll} loading={checkingAll}>
        {!checkingAll && <IconRefresh size={14} />}
        {checkingAll ? 'Checking…' : 'Check all'}
      </SecondaryButton>
      {addServerButton}
    </>
  );

  const tableHeader = (
    <div className="flex items-stretch h-10 bg-surface border-b border-line">
      <HeaderCell width={COL.handle} first>#</HeaderCell>
      <HeaderCell width={COL.name} grow align="left">Server</HeaderCell>
      <HeaderCell width={COL.status}>Status</HeaderCell>
      <HeaderCell width={COL.url} grow align="left">Health check URL</HeaderCell>
      <HeaderCell width={COL.ip}>IP address</HeaderCell>
      <HeaderCell width={COL.checked}>Last checked</HeaderCell>
      <HeaderCell width={COL.unhealthy}>Last unhealthy</HeaderCell>
      <HeaderCell width={COL.actions} sticky="right" />
    </div>
  );

  return (
    <div className="bg-page">
      <div className="w-full px-4 sm:px-8 flex flex-col gap-5">
        <PageHeader title="Server Monitoring" actions={actions} />
        <LoadingAnnouncer loading={loading} label="Loading servers" loadedLabel="Servers loaded" />

        <ErrorAlert message={error} onDismiss={() => setError(null)} />

        {loading ? (
          // Та же Card, что у готового списка: тулбар, настоящая шапка таблицы
          // и строки той же высоты — после загрузки ничего не сдвигается.
          <Card className="flex flex-col">
            <SkeletonListToolbar />
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-[1200px]">
                {tableHeader}
                <SkeletonTableRows columns={SKELETON_COLUMNS} rows={6} rowClassName={ROW} />
              </div>
            </div>
            <div className="md:hidden divide-y divide-line">
              {Array.from({ length: 3 }, (_, index) => <SkeletonListCard key={index} />)}
            </div>
          </Card>
        ) : servers.length === 0 ? (
          <Card>
            <EmptyState
              icon={<IconServer size={32} className="text-ink" />}
              title="No servers yet"
              description="Get started by adding a server to monitor."
              action={addServerButton}
            />
          </Card>
        ) : (
          <Card className="flex flex-col">
            {/* Toolbar */}
            <div className="shrink-0 flex flex-wrap items-center gap-3 p-4 border-b border-line">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by name, URL, IP…"
                width={230}
              />
              <SortSelect options={SORT_OPTIONS} value={sort} onChange={setSort} />
              <StatusSegmented
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={setStatusFilter}
                counts={counts}
              />
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-[1200px]">
                {tableHeader}

                {visibleServers.length === 0 ? (
                  <div className="py-12 text-center text-sm text-ink-muted">No servers found.</div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={visibleServers.map((s) => s._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {visibleServers.map((server) => (
                        <SortableServerRow
                          key={server._id}
                          server={server}
                          index={servers.findIndex((s) => s._id === server._id)}
                          totalCount={servers.length}
                          dragEnabled={dragEnabled}
                          onNavigate={navigate}
                          onEdit={handleEditServer}
                          onDelete={setDeleteTarget}
                          onMoveUp={handleMoveUp}
                          onMoveDown={handleMoveDown}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-line">
              {visibleServers.length === 0 ? (
                <div className="py-12 text-center text-sm text-ink-muted">No servers found.</div>
              ) : (
                visibleServers.map((server) => {
                  const index = servers.findIndex((s) => s._id === server._id);
                  return (
                    <div key={server._id} className="p-4">
                      <div className="flex justify-between items-start gap-3 mb-3">
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => navigate(`/servers/${server._id}`)}
                        >
                          <h3 className="text-[15px] font-medium text-ink truncate">{server.name}</h3>
                          {server.description && (
                            <p className="text-xs text-ink-muted mt-0.5 truncate">{server.description}</p>
                          )}
                        </div>
                        <StatusBadge status={server.lastCheckStatus} />
                      </div>

                      <div className="flex flex-col gap-2 text-xs">
                        <div>
                          <span className="text-ink-muted">Health check URL</span>
                          <a
                            href={server.healthRoute}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-brand-500 break-all hover:underline"
                          >
                            {server.healthRoute}
                          </a>
                        </div>
                        {server.ipAddress && (
                          <div>
                            <span className="text-ink-muted">IP address</span>
                            <p className="text-ink font-mono">{server.ipAddress}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-ink-muted">Last checked</span>
                          <p className="text-ink">{formatDate(server.lastCheckedAt)}</p>
                        </div>
                        <div>
                          <span className="text-ink-muted">Last unhealthy</span>
                          <p className="text-ink">{formatDate(server.lastUnhealthyCheckAt)}</p>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-3 mt-3 border-t border-line">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            aria-label="Move up"
                            className="w-8 h-8 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-ink disabled:text-ink-disabled"
                          >
                            <IconArrowUp size={14} />
                          </button>
                          <button
                            onClick={() => handleMoveDown(index)}
                            disabled={index === servers.length - 1}
                            aria-label="Move down"
                            className="w-8 h-8 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-ink disabled:text-ink-disabled"
                          >
                            <IconArrowDown size={14} />
                          </button>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditServer(server)}
                            aria-label="Edit server"
                            className="w-8 h-8 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-brand-500 hover:bg-surface"
                          >
                            <IconEdit size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(server)}
                            aria-label="Delete server"
                            className="w-8 h-8 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-danger hover:bg-surface"
                          >
                            <IconTrash size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        )}
      </div>

      {isModalOpen && (
        <ServerModal
          server={editingServer}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      <DeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        itemType="server"
        itemName={deleteTarget?.name}
      />
    </div>
  );
};

export default Servers;
